'use client';
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import type { PromotedVideoConfig } from '@/constants/promoted_videos';

interface PromotedNewsItemProps {
  config: PromotedVideoConfig;
}

const buildImpressionUrl = (template: string) =>
  template.replace('[timestamp]', String(Date.now()));

const PromotedNewsItem: React.FC<PromotedNewsItemProps> = ({ config }) => {
  const firedRef = useRef(false);

  useEffect(() => {
    // firedRef guards against React Strict-Mode double-invoke in dev.
    // On a real page visit the component mounts fresh, so this fires once.
    if (firedRef.current) return;
    firedRef.current = true;

    // Fire-and-forget impression beacon. Using a transient Image element
    // avoids React re-render concerns and works across browsers.
    try {
      const beacon = new window.Image();
      beacon.src = buildImpressionUrl(config.impressionUrl);
    } catch {
      // ignore
    }

    // Silently load the third-party verification script (e.g. IAS skeleton.js)
    // alongside the impression. Loaded fresh on every page visit (no de-dup)
    // so the verification pixel fires for each impression.
    let script: HTMLScriptElement | null = null;
    if (config.verificationScriptUrl) {
      try {
        script = document.createElement('script');
        script.type = 'application/javascript';
        script.src = config.verificationScriptUrl;
        script.async = true;
        document.head.appendChild(script);
      } catch {
        // ignore
      }
    }

    return () => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [config.impressionUrl, config.verificationScriptUrl]);

  return (
    <div className='w-full flex justify-center items-center' style={{ margin: '16px 0px' }}>
      <a
        href={config.clickUrl}
        target='_blank'
        rel='noopener noreferrer sponsored'
        className='block overflow-hidden hover:shadow-md transition-shadow duration-200'
        aria-label={config.alt || 'Sponsored'}
        style={{ width: 300, height: 250 }}
      >
        <Image
          src={config.imageUrl}
          alt={config.alt || 'Sponsored'}
          width={300}
          height={250}
          unoptimized
          priority
          style={{ width: 300, height: 250, objectFit: 'cover' }}
        />
      </a>
      {config.verificationNoScriptImageUrl ? (
        <noscript>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={config.verificationNoScriptImageUrl}
            alt=''
            width={1}
            height={1}
            style={{ border: 0 }}
          />
        </noscript>
      ) : null}
    </div>
  );
};

export default PromotedNewsItem;
